import { LogMessageDataType } from '@/common/logs/LogMessageDataType';
import { getPreferences } from '@/client/utils/PreferencesManager';
import { Log } from '@/common/logs/Log';
import { tileTypeToString } from '@/common/TileType';
const context = {
    playerView: undefined,
    players: new Map(),
};
export function setTranslationContext(playerView) {
    context.playerView = playerView;
    context.players.clear();
    for (const player of playerView.players) {
        context.players.set(player.color, player.name);
    }
}
export function translateMessage(message) {
    message.message = translateText(message.message);
    return Log.applyData(message, (datum) => {
        if (datum === undefined) {
            return '';
        }
        switch (datum.type) {
            case LogMessageDataType.PLAYER:
                return context.players.get(datum.value) ?? datum.value;
            case LogMessageDataType.TILE_TYPE:
                return tileTypeToString[datum.value];
            default:
                return translateText(String(datum.value));
        }
    });
}
let translated;
export function translateText(englishText) {
    const lang = getPreferences().lang;
    const translations = window._translations;
    if (lang === 'en' || translations === undefined) {
        return englishText;
    }
    englishText = normalizeText(englishText);
    // Don't translate non-word strings
    if (/^(\W|\d)*$/.test(englishText)) {
        return englishText;
    }
    let translatedText = translations[englishText];
    // Check if translated word is in brackets
    if (translatedText === undefined) {
        const isTextInBrackets = englishText.startsWith('(') && englishText.endsWith(')');
        if (isTextInBrackets) {
            const translationAttempt = translations[englishText.slice(1, -1)];
            if (translationAttempt) {
                translatedText = `(${translationAttempt})`;
            }
        }
    }
    if (translatedText === undefined) {
        // The i18n plugin sends translated strings back here. That means that sometimes this tries to
        // Since the phrase it sends is not English, it can't be found, and this reports an error to the
        // browser.
        //
        // This Set reduces that by seeing if the string is of the new language, and ignores reporting the error.
        if (translated === undefined) {
            translated = new Set();
            for (const k in translations) {
                if (Object.hasOwn(translations, k)) {
                    translated.add(translations[k]);
                }
            }
        }
        if (!translated.has(englishText)) {
            console.log(`${lang} - please translate: "${englishText}"`);
        }
    }
    return translatedText || englishText;
}
export function translateTextWithParams(englishText, params) {
    const data = params.map((p) => {
        return {
            type: LogMessageDataType.RAW_STRING,
            value: p,
        };
    });
    const message = {
        message: englishText,
        data: data,
    };
    return translateMessage(message);
}
/** What a route puts before its error text (see server/responses.ts). */
const STATUS_PREFIX = /^(Bad request|Not found|Forbidden|Internal server error): (?=\S)/;
let messagePatterns;
function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** The translation keys with ${n} placeholders, as regular expressions, most specific first. */
function patternsFor(translations) {
    if (messagePatterns?.source === translations) {
        return messagePatterns.patterns;
    }
    const patterns = [];
    for (const key of Object.keys(translations)) {
        if (!key.includes('${') || !translations[key]) {
            continue;
        }
        const pieces = key.split(/\$\{\d+\}/);
        const literal = pieces.join('');
        // A key that is mostly placeholders ("${0} ${1}") would match anything.
        if (literal.replace(/[^A-Za-z]/g, '').length < 4) {
            continue;
        }
        const indexes = Array.from(key.matchAll(/\$\{(\d+)\}/g), (m) => Number(m[1]));
        // Each ${n} must appear once for the match to fill it.
        if (new Set(indexes).size !== indexes.length) {
            continue;
        }
        const source = '^' + pieces.map(escapeRegExp).join('(.+?)') + '$';
        patterns.push({ key, regex: new RegExp(source), indexes, literalLength: literal.length });
    }
    patterns.sort((a, b) => b.literalLength - a.literalLength);
    messagePatterns = { source: translations, patterns };
    return patterns;
}
/**
 * Translates a message that arrives from the server as plain English, like
 * "Card Birds not found" or "Amount provided too high (max 3)".
 *
 * Exact translations come first. Otherwise the message is matched against translation keys
 * with placeholders ("Card ${0} not found"), and the values found are translated too (card
 * names, resources). A message no key matches stays as it is.
 *
 * The status prefix of a route's error text ("Bad request: …") is dropped; a bare status
 * ("Not found") is translated like any message.
 */
export function translateServerMessage(message) {
    message = message.replace(STATUS_PREFIX, '');
    const lang = getPreferences().lang;
    const translations = window._translations;
    if (lang === 'en' || translations === undefined || !message) {
        return message;
    }
    const text = normalizeText(message);
    const lookup = (key) => Object.hasOwn(translations, key) ? translations[key] : undefined;
    const exact = lookup(text);
    if (exact) {
        return exact;
    }
    const translateValue = (value) => lookup(value.trim()) || value;
    for (const pattern of patternsFor(translations)) {
        const match = pattern.regex.exec(text);
        if (match === null) {
            continue;
        }
        const values = [];
        pattern.indexes.forEach((n, i) => values[n] = translateValue(match[i + 1]));
        return translations[pattern.key].replace(/\$\{(\d+)\}/g, (all, n) => values[Number(n)] ?? all);
    }
    return message;
}
/**
 * Like translateServerMessage(), for a dialog: a message with no translation (a developer-only
 * error, see tests/locales/developerErrors.json) gets a translated line above it, so the player
 * still reads what happened in their language.
 */
export function translateServerError(message) {
    const translated = translateServerMessage(message);
    const lang = getPreferences().lang;
    if (lang === 'en' || window._translations === undefined || translated !== message.replace(STATUS_PREFIX, '')) {
        return translated;
    }
    return translateText('The server reported an unexpected error:') + '\n' + translated;
}
function normalizeText(text) {
    return text.replace(/[\n\r]/g, '').replace(/[ ]+/g, ' ').trim();
}
function translateChildren(node, params) {
    for (let i = 0, length = node.childNodes.length; i < length; i++) {
        const child = node.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) {
            const text = child;
            const translatedText = params ? translateTextWithParams(text.data, params) : translateText(text.data);
            if (translatedText !== text.data) {
                text.data = translatedText;
            }
        }
        else {
            if (child.nodeType === Node.ELEMENT_NODE && child.getAttribute('tm-has-i18n') === 'true') {
                continue;
            }
            translateChildren(child, params);
        }
    }
}
export function translateTextNode(el, binding) {
    let params = undefined;
    if (binding?.value) {
        params = binding.value instanceof Array ? binding.value : [binding.value];
    }
    translateChildren(el, params);
}
export const $t = function (msg) {
    if (!msg) {
        return '';
    }
    if (typeof msg === 'number') {
        return msg.toString();
    }
    if (typeof msg === 'string') {
        return translateText(msg);
    }
    return translateMessage(msg);
};
